import { motion } from "framer-motion";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
    {
        question: "How does the AI screening actually work?",
        answer:
            "Intellr. uses a multi-dimensional AI engine that evaluates candidates across technical skills, soft skills, and cultural fit. Our Voice AI conducts natural 10-minute screening conversations, while our resume parser extracts structured data with 99% accuracy. Both systems feed into a unified scoring model that ranks candidates objectively.",
    },
    {
        question: "Will Intellr. integrate with my existing ATS or HRMS?",
        answer:
            "Yes. Intellr. is designed to work alongside your existing tools, not replace them. We offer native integrations with popular ATS platforms and provide REST APIs for custom integrations. Most teams are up and running in under a week.",
    },
    {
        question: "Is the AI evaluation really unbiased?",
        answer:
            "Our AI uses evidence-based scoring that evaluates candidates on demonstrated skills and experience, not on name, gender, age, or background. We regularly audit our models for fairness and publish transparency reports. Every scoring decision is explainable and traceable.",
    },
    {
        question: "What does the candidate experience look like?",
        answer:
            "Candidates love it. The Voice AI interview feels like a real conversation - not a robotic questionnaire. They can schedule interviews at their convenience, get instant feedback on their application status, and track progress through a dedicated dashboard. Our candidate NPS score is 82.",
    },
    {
        question: "How fast can I see results?",
        answer:
            "Most teams see a measurable improvement within the first week. The AI begins scoring candidates immediately, and the automation engine can be configured in minutes. By week two, you'll have a fully operational AI-powered pipeline. Average time-to-hire drops by 65% within the first month.",
    },
    {
        question: "What happens to candidate data? Is it secure?",
        answer:
            "Candidate data is encrypted at rest and in transit using AES-256 and TLS 1.3. We are SOC 2 Type II compliant and follow GDPR guidelines. Data is never shared with third parties, and candidates can request deletion at any time. We conduct regular security audits and penetration testing.",
    },
    {
        question: "Do I need technical expertise to set up Intellr.?",
        answer:
            "Not at all. Intellr. is designed for HR and recruitment teams, not engineers. The setup wizard guides you through connecting your job boards, configuring screening criteria, and customizing automation rules. Most customers complete the setup in under 30 minutes without any technical help.",
    },
    {
        question: "What pricing plans are available?",
        answer:
            "We offer a free Starter plan for small teams, a Pro plan at ₹4,999/month for growing organizations, and custom Enterprise plans for large companies. All plans include AI screening, voice interviews, and pipeline automation. See our pricing page for the full comparison.",
    },
];

const FAQSection = () => {
    return (
        <section className="py-32 bg-white relative overflow-hidden">
            <div className="container mx-auto px-4 relative z-10">
                <div className="text-center mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-50 text-slate-500 text-xs font-bold uppercase tracking-wider mb-5 border border-slate-200"
                    >
                        FAQ
                    </motion.div>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                        className="font-display text-4xl md:text-6xl font-bold text-slate-900 mb-6"
                    >
                        Everything You <br />
                        <span className="text-accent italic">Need to Know</span>
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                        className="text-lg text-slate-500 max-w-xl mx-auto font-medium"
                    >
                        Common questions about getting started with AI-powered hiring.
                    </motion.p>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="max-w-3xl mx-auto"
                >
                    <Accordion type="single" collapsible className="space-y-3">
                        {faqs.map((faq, i) => (
                            <AccordionItem
                                key={i}
                                value={`faq-${i}`}
                                className="rounded-2xl border border-slate-100 bg-white px-6 py-1 shadow-sm hover:shadow-md hover:border-accent/10 transition-all data-[state=open]:border-accent/20 data-[state=open]:shadow-md"
                            >
                                <AccordionTrigger className="text-left font-display font-bold text-base text-slate-900 hover:text-accent py-5 hover:no-underline">
                                    {faq.question}
                                </AccordionTrigger>
                                <AccordionContent className="text-sm text-slate-500 leading-relaxed font-medium pb-5">
                                    {faq.answer}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </motion.div>
            </div>
        </section>
    );
};

export default FAQSection;
